"use client";

import { postData } from "@/api/api";
import { useState } from "react";
import { Mail, Send, CheckCircle, AlertCircle } from "lucide-react";

type IProps = {
  style_2?: boolean;
  style_3?: boolean;
};

const SubscribeArea = ({ style_2, style_3 }: IProps) => {
  const [toastMessage, setToastMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);
  const [focused, setFocused] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (!email) {
        setToastMessage("Please enter your email address.");
        setSuccess(false);
        return;
      }
      const resp = await postData(`/newsletters`, { email });
      if (!resp.success) {
        setToastMessage(resp?.message);
        setSuccess(false);
        return;
      }
      setToastMessage(resp?.message);
      setSuccess(true);
    } catch (error) {
      setToastMessage(error as string);
      setSuccess(false);
    } finally {
      setLoading(false);
      setEmail("");
      setTimeout(() => {
        setToastMessage("");
        setSuccess(false);
      }, 4000);
    }
  };

  return (
    <section className="sub-section">

      {/* background decor */}
      <div className="sub-orb sub-orb-1" />
      <div className="sub-orb sub-orb-2" />

      <div className="sub-container">

        {/* ── LEFT ── */}
        <div className="sub-left">
          <div className="sub-badge">
            <span className="sub-dot" />
            Newsletter
          </div>

          <h2 className="sub-heading">
            Stay Ahead of<br />
            <span className="sub-accent">Every Deal.</span>
          </h2>

          <p className="sub-desc">
            Be the first to know about exclusive discounts, new arrivals
            from top brands, and limited-time offers — straight to your inbox.
          </p>

          <ul className="sub-perks">
            {[
              "Exclusive member-only discounts",
              "Early access to new arrivals",
              "Weekly curated brand deals",
              "No spam, unsubscribe anytime",
            ].map((perk) => (
              <li key={perk} className="sub-perk">
                <span className="sub-check">✓</span>
                {perk}
              </li>
            ))}
          </ul>
        </div>

        {/* ── RIGHT: CARD ── */}
        <div className="sub-right">
          <div className="sub-card">
            <div className="sub-card-glow" />

            <div className="sub-card-body">
              <div className="sub-icon">✉</div>
              <h3 className="sub-card-title">Subscribe & Save</h3>
              <p className="sub-card-sub">Join 50,000+ smart shoppers</p>

              <form onSubmit={handleSubmit} className="sub-form">
                <div className={`sub-input-wrap ${focused ? "focused" : ""}`}>
                  <Mail className="sub-mail-icon" size={16} />
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    className="sub-input"
                  />
                </div>

                <button type="submit" disabled={loading} className="sub-btn">
                  {loading ? (
                    <span className="sub-btn-inner">
                      <span className="sub-spinner" /> Subscribing...
                    </span>
                  ) : (
                    <span className="sub-btn-inner">
                      <Send size={15} /> Subscribe Now
                    </span>
                  )}
                </button>
              </form>

              {toastMessage && (
                <div className={`sub-toast ${success ? "toast-ok" : "toast-err"}`}>
                  {success ? <CheckCircle size={15} /> : <AlertCircle size={15} />}
                  {toastMessage}
                </div>
              )}

              <p className="sub-privacy">🔒 Your data is safe. We never share it.</p>
            </div>
          </div>

          {/* trust row */}
          <div className="sub-trust">
            {[
              { num: "50K+", label: "Subscribers" },
              { num: "Daily", label: "New Deals" },
              { num: "Free", label: "Always" },
            ].map((t, i) => (
              <>
                <div key={t.label} className="sub-trust-item">
                  <span className="trust-num">{t.num}</span>
                  <span className="trust-label">{t.label}</span>
                </div>
                {i < 2 && <div className="trust-divider" />}
              </>
            ))}
          </div>
        </div>

      </div>

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800;900&display=swap');

        /* ── SECTION ── */
        .sub-section {
          font-family: 'Sora', sans-serif;
          background: #ffffff;
          padding: 100px 24px;
          position: relative;
          overflow: hidden;
        }

        .sub-orb {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
          filter: blur(90px);
        }

        .sub-orb-1 {
          top: -100px; left: -100px;
          width: 480px; height: 480px;
          background: radial-gradient(circle, rgba(255,154,60,0.10) 0%, transparent 70%);
        }

        .sub-orb-2 {
          bottom: -100px; right: -80px;
          width: 420px; height: 420px;
          background: radial-gradient(circle, rgba(255,75,110,0.08) 0%, transparent 70%);
        }

        /* ── LAYOUT ── */
        .sub-container {
          max-width: 1060px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          gap: 80px;
          position: relative;
          z-index: 2;
        }

        /* ── LEFT ── */
        .sub-left { flex: 1; }

        .sub-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255,112,67,0.08);
          border: 1px solid rgba(255,112,67,0.22);
          border-radius: 24px;
          padding: 6px 16px;
          font-size: 11px;
          font-weight: 700;
          color: #FF7043;
          letter-spacing: 2px;
          text-transform: uppercase;
          margin-bottom: 22px;
        }

        .sub-dot {
          width: 6px; height: 6px;
          border-radius: 50%;
          background: #FF7043;
          animation: sub-pulse 2s ease infinite;
        }

        @keyframes sub-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.75); }
        }

        .sub-heading {
          font-size: 50px;
          font-weight: 900;
          color: #111;
          line-height: 1.1;
          letter-spacing: -1.5px;
          margin: 0 0 18px;
        }

        .sub-accent {
          background: linear-gradient(135deg, #FFB347 0%, #FF7043 50%, #FF4B6E 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .sub-desc {
          font-size: 15px;
          color: #888;
          line-height: 1.75;
          margin: 0 0 30px;
          font-weight: 300;
          max-width: 400px;
        }

        .sub-perks {
          list-style: none;
          padding: 0; margin: 0;
          display: flex;
          flex-direction: column;
          gap: 11px;
        }

        .sub-perk {
          display: flex;
          align-items: center;
          gap: 11px;
          font-size: 13.5px;
          color: #555;
          font-weight: 400;
        }

        .sub-check {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 20px; height: 20px;
          border-radius: 50%;
          background: rgba(255,112,67,0.10);
          color: #FF7043;
          font-size: 10px;
          font-weight: 800;
          flex-shrink: 0;
        }

        /* ── RIGHT ── */
        .sub-right {
          width: 380px;
          flex-shrink: 0;
        }

        /* ── CARD ── */
        .sub-card {
          background: #ffffff;
          border: 1.5px solid #f0eeea;
          border-radius: 24px;
          padding: 40px 36px;
          position: relative;
          overflow: hidden;
          box-shadow:
            0 4px 6px rgba(134, 134, 134, 0.04),
            0 20px 48px rgba(0,0,0,0.08);
        }

        .sub-card-glow {
          position: absolute;
          top: -60px; right: -60px;
          width: 260px; height: 260px;
          background: radial-gradient(circle, rgba(255,112,67,0.10) 0%, transparent 70%);
          pointer-events: none;
        }

        .sub-card-body {
          position: relative;
          z-index: 1;
        }

        .sub-icon {
          width: 50px; height: 50px;
          background: linear-gradient(135deg, #FFB347, #FF4B6E);
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          margin-bottom: 18px;
          box-shadow: 0 8px 20px rgba(255,112,67,0.28);
        }

        .sub-card-title {
          font-size: 21px;
          font-weight: 800;
          color: #111;
          margin: 0 0 5px;
          letter-spacing: -0.5px;
        }

        .sub-card-sub {
          font-size: 13px;
          color: #aaa;
          margin: 0 0 26px;
          font-weight: 400;
        }

        /* ── FORM ── */
        .sub-form {
          display: flex;
          flex-direction: column;
          gap: 11px;
        }

        .sub-input-wrap {
          position: relative;
          display: flex;
          align-items: center;
          border: 1.5px solid #ebebeb;
          border-radius: 12px;
          background: #fafafa;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .sub-input-wrap.focused {
          border-color: #FF7043;
          box-shadow: 0 0 0 3px rgba(255,112,67,0.10);
          background: #fff;
        }

        .sub-mail-icon {
          position: absolute;
          left: 14px;
          color: #FF7043;
          pointer-events: none;
        }

        .sub-input {
          width: 100%;
          padding: 13px 13px 13px 42px;
          background: transparent;
          border: none;
          outline: none;
          font-size: 14px;
          font-family: 'Sora', sans-serif;
          color: #111;
          font-weight: 400;
        }

        .sub-input::placeholder {
          color: #ccc;
          font-weight: 300;
        }

        .sub-btn {
          width: 100%;
          padding: 14px;
          border-radius: 12px;
          border: none;
          background: linear-gradient(135deg, #FFB347 0%, #FF7043 50%, #FF4B6E 100%);
          color: #fff;
          font-size: 14px;
          font-weight: 700;
          font-family: 'Sora', sans-serif;
          cursor: pointer;
          transition: opacity 0.2s, transform 0.15s, box-shadow 0.2s;
          box-shadow: 0 6px 20px rgba(255,112,67,0.30);
          letter-spacing: 0.3px;
        }

        .sub-btn:hover:not(:disabled) {
          opacity: 0.92;
          transform: translateY(-2px);
          box-shadow: 0 10px 28px rgba(255,112,67,0.40);
        }

        .sub-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          box-shadow: none;
        }

        .sub-btn-inner {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .sub-spinner {
          display: inline-block;
          width: 14px; height: 14px;
          border: 2px solid rgba(255,255,255,0.35);
          border-top-color: white;
          border-radius: 50%;
          animation: sub-spin 0.8s linear infinite;
        }

        @keyframes sub-spin {
          to { transform: rotate(360deg); }
        }

        /* ── TOAST ── */
        .sub-toast {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 11px 14px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 500;
          margin-top: 12px;
          animation: sub-slide 0.3s ease;
        }

        .toast-ok {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          color: #166534;
        }

        .toast-err {
          background: #fff1f2;
          border: 1px solid #fecdd3;
          color: #be123c;
        }

        @keyframes sub-slide {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .sub-privacy {
          font-size: 11px;
          color: #bbb;
          text-align: center;
          margin: 14px 0 0;
          font-weight: 400;
        }

        /* ── TRUST ── */
        .sub-trust {
          display: flex;
          align-items: center;
          margin-top: 16px;
          background: #fafafa;
          border: 1.5px solid #f0eeea;
          border-radius: 14px;
          padding: 16px 24px;
        }

        .sub-trust-item {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
        }

        .trust-num {
          font-size: 16px;
          font-weight: 800;
          color: #111;
          letter-spacing: -0.5px;
        }

        .trust-label {
          font-size: 10px;
          color: #aaa;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.8px;
        }

        .trust-divider {
          width: 1px;
          height: 30px;
          background: #eee;
          flex-shrink: 0;
        }

        /* ── MOBILE ── */
        @media (max-width: 900px) {
          .sub-container {
            flex-direction: column;
            gap: 48px;
          }

          .sub-left { text-align: center; }
          .sub-badge { margin: 0 auto 22px; }
          .sub-desc { max-width: 100%; margin-left: auto; margin-right: auto; }
          .sub-perks { align-items: center; }
          .sub-right { width: 100%; max-width: 440px; }
        }

        @media (max-width: 480px) {
          .sub-section { padding: 70px 16px; }
          .sub-heading { font-size: 32px; }
          .sub-card { padding: 28px 20px; }
        }
      `}</style>
    </section>
  );
};

export default SubscribeArea;